import { useEffect, useMemo } from 'react';

import { useMutation } from '@tanstack/react-query';

import { useProfile } from 'context/profile';
import GroupType from 'models/GroupType';
import { upsertInsightsFilter } from 'services/api/preferences';

type Config = {
  preferenceKey: string;
  setSelectedGroupType: (groupType: GroupType) => void;
  resetFilters: () => void;
  selectedGroupType: GroupType | undefined;
};

export default function useInsightsGroupTypeManagement({
  preferenceKey,
  setSelectedGroupType,
  resetFilters,
  selectedGroupType,
}: Config) {
  const { profile } = useProfile();
  const { mutate: savePreference } = useMutation({ mutationFn: upsertInsightsFilter });

  const groupTypes = useMemo(() => {
    const managed = profile?.actingClient?.managedProviderTypes ?? [];
    const providedIds = new Set(profile?.actingClient?.providedProviderTypes?.map((gt) => gt.id) ?? []);
    return managed.filter((gt) => !providedIds.has(gt.id));
  }, [profile]);

  const predecessorGroupTypes = profile.actingClient.providedProviderTypes;

  const resolvedGroupType = useMemo(() => {
    if (groupTypes.length === 0) return undefined;

    const savedApiName = profile.insightsPreference?.value?.[preferenceKey];

    return (
      groupTypes.find((gt) => gt.apiName === selectedGroupType?.apiName) ||
      (savedApiName ? groupTypes.find((gt) => gt.apiName === savedApiName) : undefined) ||
      groupTypes[0]
    );
  }, [groupTypes, selectedGroupType?.apiName, profile, preferenceKey]);

  useEffect(() => {
    if (resolvedGroupType) {
      setSelectedGroupType(resolvedGroupType);
    }
  }, [resolvedGroupType, setSelectedGroupType]);

  useEffect(() => {
    resetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.actingClient.id]);

  const handleTabChange = (groupType: GroupType) => {
    if (groupType.apiName === selectedGroupType?.apiName) return;

    setSelectedGroupType(groupType);
    savePreference({
      clientId: profile.actingClient.id,
      value: {
        ...profile.insightsPreference?.value,
        [preferenceKey]: groupType.apiName,
      },
    });
  };

  return { profile, predecessorGroupTypes, groupTypes, handleTabChange };
}
